import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../supabase';
import { buscarBadges, BADGE_DEFS } from '../utils/badges';
import AvatarPremium from '../components/AvatarPremium';
import { useToast } from '../context/ToastContext';
import { Trophy, Building2, BookOpen, CheckCircle, Bell, LayoutGrid, List } from 'lucide-react';



function BibliotecaPublica() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showToast } = useToast();

  const [secaoAberta, setSecaoAberta] = useState(null);
  const [visualizacao, setVisualizacao] = useState('grade');
  const [ordenacao, setOrdenacao] = useState('az');
  const [menuOrdem, setMenuOrdem] = useState(false);
  const [busca, setBusca] = useState('');

  const [livros, setLivros] = useState([]);
  const [clubes, setClubes] = useState([]);
  const [badges, setBadges] = useState([]);
  const [carregando, setCarregando] = useState(true);

  const [perfil, setPerfil] = useState(null);
  const [donoId, setDonoId] = useState(null);
  const [reputacao, setReputacao] = useState(null);
  const [totalSeguidores, setTotalSeguidores] = useState(0);
  const [totalSeguindo, setTotalSeguindo] = useState(0);

  const [usuarioLogado, setUsuarioLogado] = useState(null);
  const [seguindo, setSeguindo] = useState(false);
  const [confirmandoUnfollow, setConfirmandoUnfollow] = useState(false);
  const unfollowTimerRef = useRef(null);

  const [avisos, setAvisos] = useState(new Set());
  const [enviandoAviso, setEnviandoAviso] = useState(null);

  const [meuWhatsapp, setMeuWhatsapp] = useState(null);
  const [modalAddWhatsapp, setModalAddWhatsapp] = useState(false);
  const [editWpp, setEditWpp] = useState('');
  const [salvandoWpp, setSalvandoWpp] = useState(false);
  const [pendingLivro, setPendingLivro] = useState(null);

  const [livroSelecionado, setLivroSelecionado] = useState(null);
  const [mensagem, setMensagem] = useState('');
  const [prazo, setPrazo] = useState(14);
  const [enviando, setEnviando] = useState(false);
  const [sucesso, setSucesso] = useState(false);

  const [badgeAberta, setBadgeAberta] = useState(null);
  const [amigosEmComum, setAmigosEmComum] = useState([]);
  const [mediaLeitor, setMediaLeitor] = useState(null);
  const [totalLeitor, setTotalLeitor] = useState(0);

  useEffect(() => { return () => clearTimeout(unfollowTimerRef.current); }, []);

  useEffect(() => {
    async function carregar() {
      const { data: { user } } = await supabase.auth.getUser();
      setUsuarioLogado(user);

      if (user) {
        const [{ data: avisosAtivos }, { data: meuPf }] = await Promise.all([
          supabase.from('avisos_disponibilidade').select('livro_id').eq('user_id', user.id).eq('ativo', true),
          supabase.from('perfis').select('whatsapp').eq('id', user.id).maybeSingle(),
        ]);
        setAvisos(new Set((avisosAtivos || []).map(a => a.livro_id)));
        setMeuWhatsapp(meuPf?.whatsapp || null);
      }

      // Busca perfil pelo slug ou @username
      let perfilQuery;
      if (slug.startsWith('@')) {
        perfilQuery = await supabase.from('perfis')
          .select('id, nome, username, slug, foto_url, plano, emprestimo_bloqueado')
          .ilike('username', slug.slice(1)).maybeSingle();
      } else {
        perfilQuery = await supabase.from('perfis')
          .select('id, nome, username, slug, foto_url, plano, emprestimo_bloqueado')
          .eq('slug', slug).maybeSingle();
      }
      const p = perfilQuery.data;
      if (!p) { setCarregando(false); return; }

      setPerfil(p);
      setDonoId(p.id);

      // Contagens sociais
      const [{ count: cSeg }, { count: cSeg2 }] = await Promise.all([
        supabase.from('seguidores').select('id', { count: 'exact', head: true }).eq('seguido_id', p.id),
        supabase.from('seguidores').select('id', { count: 'exact', head: true }).eq('seguidor_id', p.id),
      ]);
      setTotalSeguidores(cSeg || 0);
      setTotalSeguindo(cSeg2 || 0);

      // Está seguindo?
      if (user && user.id !== p.id) {
        const { data: rel } = await supabase.from('seguidores')
          .select('id').eq('seguidor_id', user.id).eq('seguido_id', p.id).maybeSingle();
        setSeguindo(!!rel);
      }

      // Livros
      const { data: livrosData } = await supabase.from('livros').select('*').eq('user_id', p.id).neq('especial', true);
      setLivros(livrosData || []);

      const livroIdParam = searchParams.get('livro');
      if (livroIdParam && livrosData) {
        const alvo = livrosData.find(l => String(l.id) === String(livroIdParam));
        if (alvo) setLivroSelecionado(alvo);
      }

      // Reputação como anfitrião
      if (livrosData?.length) {
        const { data: evals } = await supabase.from('emprestimos')
          .select('avaliacao_dono_estrelas')
          .in('livro_id', livrosData.map(l => l.id))
          .gt('avaliacao_dono_estrelas', 0);
        if (evals?.length) {
          const vals = evals.map(e => e.avaliacao_dono_estrelas);
          setReputacao({
            media: (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1),
            total: vals.length,
          });
        }
      }

      // Clubes
      const { data: membros } = await supabase.from('clube_membros')
        .select('clubes(id, nome, descricao, slug, capa_url)')
        .eq('user_id', p.id).eq('status', 'aprovado');
      setClubes((membros || []).map(m => m.clubes).filter(Boolean));

      // Badges
      const bs = await buscarBadges(p.id);
      setBadges(bs);

      // Score como leitor (como as pessoas avaliaram esta pessoa como emprestante)
      const { data: leitorEvals } = await supabase.from('emprestimos')
        .select('avaliacao_leitor_estrelas')
        .eq('leitor_user_id', p.id)
        .gt('avaliacao_leitor_estrelas', 0);
      if (leitorEvals?.length) {
        const vals = leitorEvals.map(e => e.avaliacao_leitor_estrelas);
        setMediaLeitor((vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1));
        setTotalLeitor(vals.length);
      }

      // Amigos em comum
      if (user && user.id !== p.id) {
        const [{ data: euSigo }, { data: eleSigo }] = await Promise.all([
          supabase.from('seguidores').select('seguido_id').eq('seguidor_id', user.id),
          supabase.from('seguidores').select('seguido_id').eq('seguidor_id', p.id),
        ]);
        const euSigoSet = new Set((euSigo || []).map(r => r.seguido_id));
        const comunIds = (eleSigo || []).map(r => r.seguido_id).filter(id => euSigoSet.has(id) && id !== user.id);
        if (comunIds.length > 0) {
          const { data: comunPerfis } = await supabase
            .from('perfis').select('id, nome, foto_url')
            .in('id', comunIds.slice(0, 5));
          setAmigosEmComum(comunPerfis || []);
        }
      }

      setCarregando(false);
    }
    carregar();
  }, [slug, searchParams]);

  async function handleSeguir() {
    if (!usuarioLogado) { localStorage.setItem('redirect_after_login', window.location.pathname); navigate('/login'); return; }
    await supabase.from('seguidores').insert({ seguidor_id: usuarioLogado.id, seguido_id: donoId });
    setSeguindo(true);
    setTotalSeguidores(prev => prev + 1);
    showToast(`Seguindo ${perfil?.nome || 'usuário'}!`, 'success');
    const nome = usuarioLogado.user_metadata?.nome || usuarioLogado.email?.split('@')[0] || 'Alguém';
    const { data: pf } = await supabase.from('perfis').select('username').eq('id', usuarioLogado.id).maybeSingle();
    await supabase.from('notificacoes').insert({
      user_id: donoId, tipo: 'novo_seguidor', lida: false,
      meta: { nome, username: pf?.username || null, seguidor_id: usuarioLogado.id },
    });
  }

  async function handleDeixarSeguir() {
    if (!confirmandoUnfollow) {
      setConfirmandoUnfollow(true);
      unfollowTimerRef.current = setTimeout(() => setConfirmandoUnfollow(false), 3000);
      return;
    }
    await supabase.from('seguidores').delete().eq('seguidor_id', usuarioLogado.id).eq('seguido_id', donoId);
    setSeguindo(false);
    setTotalSeguidores(prev => Math.max(0, prev - 1));
    setConfirmandoUnfollow(false);
  }

  async function handleAvisar(livroId) {
    if (!usuarioLogado || avisos.has(livroId)) return;
    setEnviandoAviso(livroId);
    await supabase.from('avisos_disponibilidade').insert({ livro_id: livroId, user_id: usuarioLogado.id, ativo: true });
    setAvisos(prev => new Set([...prev, livroId]));
    setEnviandoAviso(null);
    showToast('Avisaremos quando o livro estiver disponível!', 'success');
  }

  async function handleSalvarWhatsapp() {
    const wpp = editWpp.replace(/\D/g, '');
    if (!wpp || !usuarioLogado) return;
    setSalvandoWpp(true);
    await supabase.from('perfis').update({ whatsapp: wpp }).eq('id', usuarioLogado.id);
    setMeuWhatsapp(wpp);
    setSalvandoWpp(false);
    setModalAddWhatsapp(false);
    setEditWpp('');
    if (pendingLivro) { setLivroSelecionado(pendingLivro); setPendingLivro(null); }
  }

  function abrirSolicitar(livro) {
    if (!usuarioLogado) { localStorage.setItem('redirect_after_login', window.location.pathname); navigate('/login'); return; }
    if (!meuWhatsapp) { setPendingLivro(livro); setEditWpp(''); setModalAddWhatsapp(true); return; }
    setLivroSelecionado(livro);
  }

  async function handleSolicitar() {
    if (!usuarioLogado) return;
    const { data: meuPerfilBlq } = await supabase.from('perfis').select('emprestimo_bloqueado').eq('id', usuarioLogado.id).maybeSingle();
    if (meuPerfilBlq?.emprestimo_bloqueado) {
      showToast('Voce tem um emprestimo em atraso. Devolva o livro antes de solicitar um novo.', 'error');
      return;
    }
    setEnviando(true);
    const { error } = await supabase.from('emprestimos').insert({
      livro_id: livroSelecionado.id,
      leitor_nome: usuarioLogado.user_metadata?.nome || usuarioLogado.email.split('@')[0],
      leitor_email: usuarioLogado.email,
      leitor_user_id: usuarioLogado.id,
      mensagem,
      prazo_dias: prazo,
      status: 'pendente',
    });
    if (!error && donoId) {
      const { data: emprestimo } = await supabase
        .from('emprestimos')
        .select('id')
        .eq('livro_id', livroSelecionado.id)
        .eq('leitor_user_id', usuarioLogado.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();
      const { data: meuPerfil } = await supabase.from('perfis').select('nome').eq('id', usuarioLogado.id).maybeSingle();
      const leitorNome = meuPerfil?.nome || usuarioLogado.user_metadata?.nome || usuarioLogado.email?.split('@')[0] || 'Alguém';
      await supabase.from('notificacoes').insert({
        user_id: donoId, emprestimo_id: emprestimo?.id || null, tipo: 'nova_solicitacao', lida: false,
        meta: { livro_titulo: livroSelecionado?.titulo, leitor_nome: leitorNome },
      });
    }
    setEnviando(false);
    if (!error) setSucesso(true);
  }

  const ordenacoes = { az: 'A–Z', stars: 'Avaliação', disp: 'Disponíveis' };
  const livrosOrdenados = [...livros].sort((a, b) => {
    if (ordenacao === 'az') return a.titulo.localeCompare(b.titulo);
    if (ordenacao === 'stars') return b.estrelas - a.estrelas;
    if (ordenacao === 'disp') return b.disponivel - a.disponivel;
    return 0;
  });
  const livrosFiltrados = busca.trim()
    ? livrosOrdenados.filter(l => {
        const q = busca.toLowerCase();
        return l.titulo?.toLowerCase().includes(q) || l.autor?.toLowerCase().includes(q);
      })
    : livrosOrdenados;

  const isOwnProfile = usuarioLogado && donoId && usuarioLogado.id === donoId;


  // ── Perfil completo ───────────────────────────────────────────────────────
  const cardShadow = '0 1px 2px rgba(33,28,43,0.04),0 4px 16px rgba(33,28,43,0.05)';

  return (
    <div className="min-h-screen bg-paper font-sans" onClick={() => setMenuOrdem(false)}>

      {/* Back header — sticky frosted */}
      <div className="sticky top-0 z-10 flex items-center gap-3 px-[18px] pb-3"
        style={{paddingTop:'calc(env(safe-area-inset-top) + 14px)', background:'rgba(250,248,245,0.88)', backdropFilter:'blur(14px) saturate(160%)', WebkitBackdropFilter:'blur(14px) saturate(160%)', borderBottom:'1px solid #EBE8F0'}}>
        <button onClick={() => navigate(-1)}
          className="w-[38px] h-[38px] rounded-[12px] border border-[#EBE8F0] bg-white flex items-center justify-center text-ink-700 flex-shrink-0">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6"/></svg>
        </button>
        {perfil && (
          <>
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <div className="w-[26px] h-[26px] rounded-[8px] overflow-hidden flex-shrink-0 flex items-center justify-center text-[11px] font-bold text-white"
                style={{background:'linear-gradient(150deg,#6E50F0,#3F27B0)'}}>
                {perfil.foto_url
                  ? <img src={perfil.foto_url} alt="" className="w-full h-full object-cover" />
                  : (perfil.nome || 'U').charAt(0).toUpperCase()}
              </div>
              <span className="text-[15px] font-bold text-ink-900 truncate">{perfil.nome}</span>
            </div>
            {!isOwnProfile && usuarioLogado && (
              <button
                onClick={seguindo ? handleDeixarSeguir : handleSeguir}
                className="flex-shrink-0 text-[12px] font-bold px-3.5 py-1.5 rounded-[10px] border-0 cursor-pointer transition-colors"
                style={seguindo
                  ? (confirmandoUnfollow ? {background:'#FEE2E2', color:'#DC2626'} : {background:'#EDE8FC', color:'#5B3CE4'})
                  : {background:'#5B3CE4', color:'white'}}>
                {seguindo ? (confirmandoUnfollow ? 'Deixar?' : 'Seguindo') : 'Seguir'}
              </button>
            )}
          </>
        )}
        {!perfil && !carregando && (
          <span className="text-[15px] font-bold text-ink-400 flex-1">Perfil não encontrado</span>
        )}
      </div>

      <div className="max-w-5xl mx-auto pb-16">

        {carregando ? (
          <p className="text-sm text-ink-400 text-center pt-16">Carregando...</p>
        ) : !perfil ? (
          <div className="text-center pt-20 px-5">
            <p className="text-[15px] font-semibold text-ink-900 mb-1">Perfil não encontrado</p>
            <p className="text-[13px] text-ink-400">Esse usuário não existe ou foi removido.</p>
          </div>
        ) : (
          <>
            {/* Profile hero */}
            <div className="text-center px-5 pt-7 pb-5">
              <div className="mx-auto mb-3.5 flex items-center justify-center" style={{width:86,height:86}}>
                <AvatarPremium fotoUrl={perfil.foto_url} nome={perfil.nome} isPremium={perfil.plano === 'pago'} size={86} radius="28px" style={{boxShadow:'0 10px 24px rgba(91,60,228,0.32)'}} />
              </div>
              <h1 className="text-[22px] font-extrabold tracking-tight m-0 mb-0.5 text-ink-900">{perfil.nome}</h1>
              {perfil.username && (
                <p className="text-[13.5px] font-bold m-0 mb-3.5" style={{color:'#5B3CE4'}}>@{perfil.username}</p>
              )}
              {perfil.emprestimo_bloqueado && (
                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full mb-3" style={{background:'#FEF2F2', border:'1px solid #FECACA'}}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                  <span className="text-[12px] font-bold" style={{color:'#DC2626'}}>Inadimplente</span>
                </div>
              )}
              <div className="flex items-center justify-center gap-2.5 flex-wrap">
                {mediaLeitor && (
                  <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full" style={{background:'#FBF1DC'}}>
                    <span style={{color:'#FFAA00', fontSize:15}}>★</span>
                    <span className="text-[13px] font-extrabold" style={{color:'#9A6B12'}}>{mediaLeitor}</span>
                    <span className="text-[11px] font-semibold text-ink-400">leitor</span>
                    {totalLeitor > 0 && <span className="text-[11px] font-semibold text-ink-300">({totalLeitor})</span>}
                  </div>
                )}
                {reputacao && (
                  <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full" style={{background:'#FBF1DC'}}>
                    <span style={{color:'#FFAA00', fontSize:15}}>★</span>
                    <span className="text-[13px] font-extrabold" style={{color:'#9A6B12'}}>{reputacao.media}</span>
                    <span className="text-[11px] font-semibold text-ink-400">anfitrião</span>
                    {reputacao.total > 0 && <span className="text-[11px] font-semibold text-ink-300">({reputacao.total})</span>}
                  </div>
                )}
                {badges.slice(0, 5).map(b => b.Icon ? (
                  <span key={b.tipo} className={`flex items-center justify-center w-7 h-7 rounded-[8px] border ${b.cor}`}>
                    <b.Icon size={13} />
                  </span>
                ) : null)}
                {badges.length > 5 && (
                  <span className="text-[11px] font-bold text-ink-400">+{badges.length - 5}</span>
                )}
              </div>
              {amigosEmComum.length > 0 && (
                <div className="flex items-center justify-center gap-2 mt-3.5">
                  <div className="flex">
                    {amigosEmComum.slice(0, 3).map((a, i) => (
                      <div key={a.id}
                        className="w-[22px] h-[22px] rounded-full border-2 border-white overflow-hidden flex items-center justify-center text-[9px] font-bold text-white flex-shrink-0"
                        style={{background:'linear-gradient(150deg,#6E50F0,#3F27B0)', marginLeft: i > 0 ? -6 : 0, zIndex: 3 - i}}>
                        {a.foto_url
                          ? <img src={a.foto_url} alt="" className="w-full h-full object-cover" />
                          : (a.nome || 'U').charAt(0).toUpperCase()}
                      </div>
                    ))}
                  </div>
                  <span className="text-[12px] font-semibold text-ink-400">
                    {amigosEmComum.length} amigo{amigosEmComum.length !== 1 ? 's' : ''} em comum
                  </span>
                </div>
              )}
            </div>

            {/* CTA buttons */}
            {!isOwnProfile && (
              <div className="flex gap-3 px-5 mb-5">
                {!usuarioLogado ? (
                  <button onClick={() => navigate('/login')}
                    className="flex-1 py-3 text-[14px] font-bold rounded-[14px] border-0 cursor-pointer text-white"
                    style={{background:'#5B3CE4'}}>
                    Entrar para seguir
                  </button>
                ) : !seguindo ? (
                  <button onClick={handleSeguir}
                    className="flex-1 py-3 text-[14px] font-bold rounded-[14px] border-0 cursor-pointer text-white"
                    style={{background:'#5B3CE4'}}>
                    Seguir
                  </button>
                ) : (
                  <button onClick={handleDeixarSeguir}
                    className="flex-1 py-3 text-[14px] font-bold rounded-[14px] cursor-pointer transition-colors"
                    style={confirmandoUnfollow ? {background:'#FEE2E2', color:'#DC2626', border:'1px solid #FECACA'} : {background:'#EDE8FC', color:'#5B3CE4', border:'none'}}>
                    {confirmandoUnfollow ? 'Deixar de seguir?' : 'Seguindo'}
                  </button>
                )}
              </div>
            )}

            {/* Stats row */}
            <div className="flex bg-white border border-line rounded-[18px] mx-5 mb-5" style={{boxShadow: cardShadow}}>
              {[
                [livros.length, 'livros'],
                [totalSeguidores, 'seguidores'],
                [totalSeguindo, 'seguindo'],
              ].map(([n, l], i) => (
                <div key={i} className="flex-1 text-center py-4 relative">
                  {i > 0 && <div className="absolute left-0 top-[22%] bottom-[22%] w-px bg-line" />}
                  <div className="text-[20px] font-extrabold tracking-tight text-ink-900">{n}</div>
                  <div className="text-[11.5px] text-ink-400 font-semibold mt-0.5">{l}</div>
                </div>
              ))}
            </div>

            {/* Accordion — conquistas + clubes */}
            {(badges.length > 0 || clubes.length > 0) && (
              <div className="mx-5 mb-5 bg-white border border-line rounded-[18px] overflow-hidden" style={{boxShadow: cardShadow}}>

                {/* Conquistas */}
                {badges.length > 0 && (
                  <>
                    <button onClick={() => setSecaoAberta(secaoAberta === 'conquistas' ? null : 'conquistas')}
                      className="w-full flex items-center gap-3 px-4 py-[15px] bg-transparent border-0 cursor-pointer text-left">
                      <Trophy size={18} color="#5B3CE4" />
                      <span className="flex-1 text-[14.5px] font-semibold text-ink-900">Conquistas</span>
                      <span className="flex items-center gap-0.5 mr-1">
                        {badges.slice(0,3).map(b => b.Icon ? <b.Icon key={b.tipo} size={13} color="#5B3CE4" /> : null)}
                        {badges.length > 3 && <span className="text-[11px] text-ink-400 ml-0.5">+{badges.length-3}</span>}
                      </span>
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#BDB7C5" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        {secaoAberta === 'conquistas' ? <polyline points="18 15 12 9 6 15"/> : <polyline points="6 9 12 15 18 9"/>}
                      </svg>
                    </button>
                    {secaoAberta === 'conquistas' && (
                      <div style={{borderTop:'1px solid #F2EEE9'}} className="p-4">
                        <div className="flex items-center justify-between mb-3">
                          <p className="text-[11.5px] font-extrabold uppercase tracking-[0.05em] text-ink-400 m-0">Conquistas</p>
                          <p className="text-[11.5px] text-ink-400 m-0">{badges.length} desbloqueada{badges.length !== 1 ? 's' : ''}</p>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          {badges.map(b => {
                            const def = BADGE_DEFS.find(d => d.tipo === b.tipo);
                            const isOpen = badgeAberta === b.tipo;
                            if (!def) return null;
                            return (
                              <button key={b.tipo}
                                onClick={() => setBadgeAberta(isOpen ? null : b.tipo)}
                                className={`bg-white border rounded-[18px] p-4 text-left transition-all ${def.cor}`}>
                                <div className={`w-11 h-11 rounded-[13px] flex items-center justify-center mb-2.5 border ${def.cor}`}>
                                  {def.Icon && <def.Icon size={20} />}
                                </div>
                                <p className="text-[12.5px] font-semibold text-ink-900 leading-snug m-0">{def.nome}</p>
                                {isOpen
                                  ? <p className="text-xs text-ink-500 mt-1.5 leading-relaxed">{def.desc}</p>
                                  : <p className="text-xs mt-1 text-success">desbloqueada</p>
                                }
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* Clubes */}
                {clubes.length > 0 && (
                  <div style={badges.length > 0 ? {borderTop:'1px solid #F2EEE9'} : {}}>
                    <button onClick={() => setSecaoAberta(secaoAberta === 'clubes' ? null : 'clubes')}
                      className="w-full flex items-center gap-3 px-4 py-[15px] bg-transparent border-0 cursor-pointer text-left">
                      <Building2 size={18} color="#5B3CE4" />
                      <span className="flex-1 text-[14.5px] font-semibold text-ink-900">Clubes do Livro</span>
                      <span className="text-[12px] text-ink-400 font-semibold mr-1">{clubes.length}</span>
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#BDB7C5" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        {secaoAberta === 'clubes' ? <polyline points="18 15 12 9 6 15"/> : <polyline points="6 9 12 15 18 9"/>}
                      </svg>
                    </button>
                    {secaoAberta === 'clubes' && (
                      <div style={{borderTop:'1px solid #F2EEE9'}} className="p-4 flex flex-col gap-3">
                        {clubes.map(clube => (
                          <div key={clube.id} className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-[12px] flex items-center justify-center overflow-hidden flex-shrink-0"
                              style={{background:'#EDE8FC'}}>
                              {clube.capa_url
                                ? <img src={clube.capa_url} alt="" className="w-full h-full object-cover" />
                                : <Building2 size={16} color="#5B3CE4" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-[13.5px] font-semibold text-ink-900 m-0 truncate">{clube.nome}</p>
                              {clube.descricao && <p className="text-[11.5px] text-ink-400 m-0 truncate">{clube.descricao}</p>}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

              </div>
            )}

            {/* Acervo */}
            <div className="px-4 pb-4">
              <p className="text-[11.5px] font-extrabold uppercase tracking-[0.05em] text-ink-400 m-0 mb-3">Acervo</p>

              <div className="relative mb-3">
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-300" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                <input
                  type="text"
                  placeholder="Buscar por título ou autor..."
                  value={busca}
                  onChange={e => setBusca(e.target.value)}
                  className="w-full bg-white border border-line-soft rounded-xl pl-9 pr-8 py-2.5 text-sm outline-none focus:border-line text-ink-900 placeholder-ink-300"
                />
                {busca && (
                  <button onClick={() => setBusca('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-300 hover:text-ink-500">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  </button>
                )}
              </div>

              <div className="flex items-center justify-between mb-3">
                <p className="text-xs text-ink-400">{livrosFiltrados.length} livro{livrosFiltrados.length !== 1 ? 's' : ''} · {livros.filter(l => l.disponivel).length} disponíve{livros.filter(l => l.disponivel).length !== 1 ? 'is' : 'l'}</p>
                <div className="flex items-center gap-3" onClick={e => e.stopPropagation()}>
                  <div className="relative">
                    <button onClick={() => setMenuOrdem(!menuOrdem)}
                      className="flex items-center gap-1 text-xs text-ink-400 hover:text-ink-500">
                      <span>{ordenacoes[ordenacao]}</span><span className="text-ink-300">↕</span>
                    </button>
                    {menuOrdem && (
                      <div className="absolute right-0 top-6 bg-white border border-line-soft rounded-xl shadow-sm z-10 min-w-32 overflow-hidden">
                        {Object.entries(ordenacoes).map(([key, label]) => (
                          <button key={key} onClick={() => { setOrdenacao(key); setMenuOrdem(false); }}
                            className={`w-full text-left px-4 py-2.5 text-xs hover:bg-paper ${ordenacao === key ? 'text-ink-900 font-medium' : 'text-ink-400'}`}>
                            {label} {ordenacao === key && '·'}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="w-px h-3 bg-line" />
                  <button onClick={() => setVisualizacao(v => v === 'grade' ? 'lista' : 'grade')}
                    className="text-xs text-ink-400 hover:text-ink-500">
                    {visualizacao === 'grade' ? <LayoutGrid size={14} /> : <List size={14} />}
                  </button>
                </div>
              </div>

              {livros.length === 0 ? (
                <div className="text-center py-16">
                  <BookOpen size={40} color="#C4BCDA" strokeWidth={1} className="mb-3" />
                  <p className="text-sm text-ink-400">Nenhum livro cadastrado ainda.</p>
                </div>
              ) : livrosFiltrados.length === 0 ? (
                <div className="text-center py-16">
                  <div className="w-12 h-12 rounded-[16px] flex items-center justify-center mx-auto mb-3" style={{background:'#EDE9FF'}}>
                    <BookOpen size={22} strokeWidth={1.5} color="#5B3CE4" />
                  </div>
                  <p className="text-[14px] font-bold text-ink-900 mb-1">Nenhum resultado</p>
                  <p className="text-[12.5px] text-ink-400">Tente buscar por outro título ou autor.</p>
                </div>
              ) : visualizacao === 'grade' ? (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  {livrosFiltrados.map(livro => (
                    <div key={livro.id} className="bg-white border border-line-soft rounded-xl overflow-hidden">
                      <div className="h-28 bg-paper flex items-center justify-center overflow-hidden">
                        {livro.capa_url
                          ? <img src={livro.capa_url} alt="" className="w-full h-full object-cover" />
                          : <BookOpen size={32} color="#C4BCDA" strokeWidth={1} />}
                      </div>
                      <div className="p-3">
                        <p className="text-xs font-medium text-ink-900 leading-tight line-clamp-2">{livro.titulo}</p>
                        <p className="text-xs text-ink-400 mt-0.5 truncate">{livro.autor}</p>
                        {livro.estrelas > 0 && (
                          <div className="flex mt-1">
                            {[1,2,3,4,5].map(s => (
                              <span key={s} className={`text-xs ${s <= livro.estrelas ? 'text-amber-400' : 'text-ink-300'}`}>★</span>
                            ))}
                          </div>
                        )}
                        <span className={`inline-block text-xs px-2 py-0.5 rounded-full mt-2 ${livro.disponivel ? 'bg-paper-2 text-ink-500' : 'bg-paper text-ink-400'}`}>
                          {livro.disponivel ? 'Disponível' : 'Emprestado'}
                        </span>
                        {livro.disponivel && (
                          <button onClick={() => { abrirSolicitar(livro); }}
                            className="w-full mt-2 py-1.5 text-xs bg-brand text-white rounded-lg hover:bg-brand-dark transition-colors">
                            {usuarioLogado ? 'Pedir emprestado' : 'Entrar para pedir'}
                          </button>
                        )}
                        {!livro.disponivel && usuarioLogado && (
                          <button onClick={() => handleAvisar(livro.id)} disabled={avisos.has(livro.id) || enviandoAviso === livro.id}
                            className="w-full mt-2 py-1.5 text-xs border border-line-soft text-ink-400 rounded-lg hover:bg-paper transition-colors disabled:opacity-60 disabled:cursor-default">
                            <span className="flex items-center justify-center gap-1"><Bell size={11} />{avisos.has(livro.id) ? 'Avisarei você' : 'Avise quando chegar'}</span>
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col">
                  {livrosFiltrados.map(livro => (
                    <div key={livro.id} className="flex items-center gap-3 py-3 border-b border-line-soft">
                      <div className="w-9 h-12 bg-paper rounded overflow-hidden flex items-center justify-center text-base flex-shrink-0">
                        {livro.capa_url
                          ? <img src={livro.capa_url} alt="" className="w-full h-full object-cover" />
                          : <BookOpen size={14} color="#C4BCDA" strokeWidth={1} />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-ink-900 truncate">{livro.titulo}</p>
                        <p className="text-xs text-ink-400 mt-0.5 truncate">{livro.autor}</p>
                        {livro.estrelas > 0 && (
                          <p className="text-xs text-amber-400">{[...Array(livro.estrelas)].map(() => '★').join('')}</p>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-1 flex-shrink-0">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${livro.disponivel ? 'bg-paper-2 text-ink-500' : 'bg-paper text-ink-400'}`}>
                          {livro.disponivel ? 'Disponível' : 'Emprestado'}
                        </span>
                        {livro.disponivel && (
                          <button onClick={() => { abrirSolicitar(livro); }}
                            className="text-xs text-brand font-medium">
                            Pedir →
                          </button>
                        )}
                        {!livro.disponivel && usuarioLogado && !avisos.has(livro.id) && (
                          <button onClick={() => handleAvisar(livro.id)} disabled={enviandoAviso === livro.id}
                            className="text-xs text-ink-400 disabled:opacity-60">
                            <span className="flex items-center gap-1"><Bell size={11} />Avisar</span>
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Modal de solicitação de empréstimo */}
      {livroSelecionado && (
        <div className="fixed inset-0 z-50 flex items-end lg:items-center justify-center bg-black/40 px-0 lg:px-4">
          <div className="w-full lg:max-w-md bg-white rounded-t-3xl lg:rounded-2xl shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-gray-50">
              <p className="text-[15px] font-bold text-ink-900">Solicitar empréstimo</p>
              <button onClick={() => { setLivroSelecionado(null); setSucesso(false); }}
                className="w-8 h-8 rounded-full flex items-center justify-center text-ink-400 hover:text-ink-700 hover:bg-paper transition-colors text-xl leading-none">
                ×
              </button>
            </div>
            <div className="px-5 pb-6 pt-4">
              {sucesso ? (
                <div className="text-center py-4">
                  <CheckCircle size={44} color="#1FA971" strokeWidth={1.5} className="mb-3 mx-auto" />
                  <p className="text-base font-semibold text-ink-900 mb-1">Solicitação enviada!</p>
                  <p className="text-sm text-ink-400 mb-5">Aguarde {perfil?.nome} aprovar o empréstimo.</p>
                  <button onClick={() => navigate('/home')}
                    className="w-full bg-brand text-white py-3 rounded-xl text-sm font-semibold hover:bg-brand-dark transition-colors">
                    Ver no meu acervo
                  </button>
                </div>
              ) : (
                <div className="flex flex-col gap-4">
                  <div className="flex gap-3 pb-4 border-b border-gray-50">
                    <div className="w-10 h-14 bg-paper rounded overflow-hidden flex items-center justify-center flex-shrink-0">
                      {livroSelecionado.capa_url
                        ? <img src={livroSelecionado.capa_url} alt="" className="w-full h-full object-cover" />
                        : <BookOpen size={20} color="#C4BCDA" strokeWidth={1} />}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-ink-900">{livroSelecionado.titulo}</p>
                      <p className="text-xs text-ink-400 mt-0.5">{livroSelecionado.autor}</p>
                    </div>
                  </div>
                  <div className="bg-paper rounded-xl px-4 py-3">
                    <p className="text-xs text-ink-400">Solicitando como</p>
                    <p className="text-sm font-medium text-ink-900 mt-0.5">{usuarioLogado?.user_metadata?.nome || usuarioLogado?.email}</p>
                  </div>
                  <div>
                    <label className="text-xs text-ink-400 mb-1 block">Prazo desejado</label>
                    <select value={prazo} onChange={e => setPrazo(parseInt(e.target.value))}
                      className="w-full border border-line-soft rounded-xl px-4 py-3 text-sm bg-paper outline-none focus:border-line">
                      <option value={7}>1 semana</option>
                      <option value={14}>2 semanas</option>
                      <option value={21}>3 semanas</option>
                      <option value={30}>1 mês</option>
                      <option value={0}>Sem prazo definido</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-ink-400 mb-1 block">Mensagem <span className="text-ink-300">(opcional)</span></label>
                    <textarea placeholder="Oi! Posso pegar emprestado?" value={mensagem} onChange={e => setMensagem(e.target.value)}
                      rows={3} className="w-full border border-line-soft rounded-xl px-4 py-3 text-sm bg-paper outline-none focus:border-line resize-none" />
                  </div>
                  <button onClick={handleSolicitar} disabled={enviando}
                    className="w-full bg-brand text-white py-3 rounded-xl text-sm font-semibold hover:bg-brand-dark transition-colors disabled:opacity-50">
                    {enviando ? 'Enviando...' : 'Solicitar empréstimo'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal WhatsApp obrigatório */}
      {modalAddWhatsapp && (
        <div className="fixed inset-0 z-50 flex items-end lg:items-center justify-center bg-black/40 px-4 pb-6 lg:pb-0">
          <div className="w-full max-w-sm bg-white rounded-2xl p-6 shadow-xl">
            <h3 className="text-[17px] font-extrabold text-ink-900 mb-1">Adicione seu WhatsApp</h3>
            <p className="text-[13px] text-ink-400 mb-5 leading-5">
              Para solicitar empréstimos, você precisa ter WhatsApp cadastrado. Isso permite que o dono entre em contato com você após a aprovação.
            </p>
            <label className="text-[12px] font-semibold text-ink-400 mb-1 block">Número (só dígitos, com DDD)</label>
            <input
              type="tel"
              value={editWpp}
              onChange={e => setEditWpp(e.target.value.replace(/\D/g, '').slice(0, 11))}
              placeholder="11999999999"
              className="w-full border border-line rounded-xl px-4 py-3 text-[14px] text-ink-900 outline-none focus:border-brand mb-4"
              autoFocus
            />
            <div className="flex gap-2">
              <button
                onClick={handleSalvarWhatsapp}
                disabled={salvandoWpp || editWpp.replace(/\D/g, '').length < 10}
                className="flex-1 py-3 rounded-[14px] text-[14px] font-extrabold text-white disabled:opacity-50"
                style={{background:'#5B3CE4'}}>
                {salvandoWpp ? 'Salvando...' : 'Salvar e continuar'}
              </button>
              <button
                onClick={() => { setModalAddWhatsapp(false); setPendingLivro(null); }}
                className="flex-1 py-3 rounded-[14px] text-[14px] font-semibold border border-line text-ink-500">
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BibliotecaPublica;
